import { type ClientOutstandingRow } from '@utazzo/shared';
import { UzInput } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useClientOutstanding } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Client Outstanding - report #10. Active invoices where billed > received, with
 * days outstanding. Built on the shared ReportListScreen shell.
 */

const rupees = (paise: string): number => Number(paise) / 100;
const sumPaise = (
  rows: ReadonlyArray<ClientOutstandingRow>,
  pick: (r: ClientOutstandingRow) => string,
) => formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

const money = (
  header: string,
  pick: (r: ClientOutstandingRow) => string,
): ReportColumn<ClientOutstandingRow> => ({
  header,
  align: 'right',
  value: (r) => rupees(pick(r)),
  render: (r) => formatInrPaise(pick(r)),
  total: (rows) => sumPaise(rows, pick),
});

export const ClientOutstandingPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [q, setQ] = useState('');

  const query = { from, to, ...(q.trim() ? { search: q.trim() } : {}) };
  const reportQuery = useClientOutstanding(query);
  const rows = reportQuery.data?.rows ?? [];

  const columns: ReportColumn<ClientOutstandingRow>[] = [
    {
      header: 'Invoice No.',
      value: (r) => r.invoiceNumber,
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.invoiceNumber}
        </Link>
      ),
    },
    { header: 'Date', value: (r) => formatDateDMY(r.invoiceDate ?? '') },
    { header: 'Customer', value: (r) => r.customerName ?? '' },
    { header: 'Email', value: (r) => r.customerEmail ?? '' },
    { header: 'Agent', value: (r) => r.agentName ?? '' },
    money('Grand Total', (r) => r.grandTotalPaise),
    money('Received', (r) => r.receivedPaise),
    money('Outstanding', (r) => r.outstandingPaise),
    {
      header: 'Days',
      align: 'right',
      value: (r) => r.daysOutstanding,
      render: (r) => String(r.daysOutstanding),
    },
  ];

  return (
    <ReportListScreen
      title="Client Outstanding"
      description="Invoices where the customer still owes money, with days outstanding."
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.invoiceId}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No invoices are outstanding for these filters."
      exportName="client-outstanding"
      search={
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search invoice / customer / email / agent..."
          containerClassName="max-w-md"
        />
      }
      filters={<FyRangeControls state={fyState} />}
    />
  );
};
